import * as moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import 'moment/locale/de';
import { cn } from '@utils/helpers';
import { ChevronDownIcon } from 'lucide-react';
import { PublicationsMonthGroup } from './PublicationsMonthGroup';
import type { PublicationDocumentWithYear, PublicationsYearGroupProps } from './types';

export const PublicationsYearGroup: React.FC<PublicationsYearGroupProps> = ({ documents, yearLabel, year, isFirst, language, variousFundsText }) => {
  const [isVisible, setIsVisible] = useState(isFirst);

  useEffect(() => {
    if (isFirst) {
      setIsVisible(true);
    }
  }, [isFirst]);

  const groupDocumentsByMonth = useCallback(
    (docs: PublicationDocumentWithYear[]): { [key: number]: PublicationDocumentWithYear[] } =>
      docs.reduce<{ [key: number]: PublicationDocumentWithYear[] }>((prev, doc) => {
        const month = Number.parseInt(moment(doc.fileDate, 'YYYY-MM-DD').locale(language).format('M'), 10);

        if (prev[month] !== undefined) {
          prev[month].push(doc);
        } else {
          prev[month] = [doc];
        }
        return prev;
      }, {}),
    [language],
  );

  const toggleVisibility = useCallback((): void => {
    setIsVisible((prev) => !prev);
  }, []);

  const groupedDocuments = useMemo(() => groupDocumentsByMonth(documents), [documents, groupDocumentsByMonth]);

  const sortedMonths = useMemo(() => Object.keys(groupedDocuments).sort((a, b) => (Number.parseInt(a, 10) < Number.parseInt(b, 10) ? 1 : -1)), [groupedDocuments]);

  return (
    <div>
      <h4
        onClick={toggleVisibility}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleVisibility();
          }
        }}
        className="cursor-pointer border-b border-[#ececec] pb-5 text-xl text-gray-500 flex items-center gap-6"
      >
        <span>{`${yearLabel} ${year}`}</span>
        <ChevronDownIcon className={cn('size-4 text-gray-500', isVisible && 'rotate-180')} />
      </h4>
      <div className="pt-5">
        <div className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out ${isVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
            {sortedMonths.map((month, index) => (
              <PublicationsMonthGroup
                key={month}
                documents={groupedDocuments[Number.parseInt(month, 10)]}
                month={moment(month, 'M').locale(language).format('MMMM')}
                isFirst={isFirst && index === 0}
                language={language}
                variousFundsText={variousFundsText}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
